import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { Fragment, ReactNode, type JSX } from 'react';
import { getDialcodes } from '@repo/common-utils/countries';
import CountrySelect from 'src/components/common/country-select/CountrySelect';
import PhoneInput from 'src/components/common/phone-input/PhoneInput';
import { useLocale } from '@repo/i18n';
import { Iso2Code, PhoneNumber, CompanyInfo } from '@repo/types';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import { capitalize } from '@repo/common-utils/TextUtils';
import { validateCompanyInfo } from '@repo/widget-utils/checkout-info-helper';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type Props = {
    companyInfo: CompanyInfo;
    onChange: (change: Partial<CompanyInfo>) => void;
    showErrors: boolean;
};

// Static IDs and error props for form fields (singleton component - no dynamic IDs needed)
const FIELD = {
    companyName: {
        id: 'company-companyName',
        errorProps: { id: 'company-companyName-error', role: 'alert' as const },
    },
    contactPerson: {
        id: 'company-contactPerson',
        errorProps: { id: 'company-contactPerson-error', role: 'alert' as const },
    },
    orgNumber: {
        id: 'company-orgNumber',
        errorProps: { id: 'company-orgNumber-error', role: 'alert' as const },
    },
    email: {
        id: 'company-email',
        errorProps: { id: 'company-email-error', role: 'alert' as const },
    },
    address: {
        id: 'company-address',
        errorProps: { id: 'company-address-error', role: 'alert' as const },
    },
    postCode: {
        id: 'company-postCode',
        errorProps: { id: 'company-postCode-error', role: 'alert' as const },
    },
    city: {
        id: 'company-city',
        errorProps: { id: 'company-city-error', role: 'alert' as const },
    },
    privacy: {
        errorId: 'company-privacy-error',
        invalidProps: {
            'aria-invalid': true as const,
            'aria-describedby': 'company-privacy-error',
        },
    },
    terms: {
        errorId: 'company-terms-error',
        invalidProps: { 'aria-invalid': true as const, 'aria-describedby': 'company-terms-error' },
    },
} as const;

export default function CompanyInfoForm(props: Props): ReactNode {
    const { t } = useLocale();
    const configurations = useConfigurations();
    const { companyInfo, onChange } = props;

    const {
        isContactPersonValid,
        isOrgNumberValid,
        isCompanyNameValid,
        isPhoneDialValid,
        isPhoneNumberValid,
        isEmailValid,
        isAddressValid,
        isPostCodeValid,
        isCityValid,
    } = validateCompanyInfo(companyInfo);

    const showHasAcceptedPrivacyValidationError =
        !companyInfo.hasAcceptedPrivacy && props.showErrors;
    const showHasAcceptedTermsValidationError = !companyInfo.hasAcceptedTerms && props.showErrors;
    const showCompanyNameValidationError = !isCompanyNameValid && props.showErrors;
    const showOrganizationNumberValidationError = !isOrgNumberValid && props.showErrors;
    const showContactPersonValidationError = !isContactPersonValid && props.showErrors;
    const showPhoneDialCodeValidationError = !isPhoneDialValid && props.showErrors;
    const showPhoneNumberValidationError = !isPhoneNumberValid && props.showErrors;
    const showEmailValidationError = !isEmailValid && props.showErrors;
    const showAddressValidationError = !isAddressValid && props.showErrors;
    const showPostCodeValidationError = !isPostCodeValid && props.showErrors;
    const showCityValidationError = !isCityValid && props.showErrors;

    const errorStyles = {
        color: '#f44336',
        '& > *': {
            color: '#f44336',
        },
    };

    return (
        <Fragment>
            <FormControl>
                <InputLabel
                    htmlFor={FIELD.companyName.id}
                    error={showCompanyNameValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.company_name)}
                </InputLabel>
                <TextField
                    id={FIELD.companyName.id}
                    required={true}
                    error={showCompanyNameValidationError}
                    aria-invalid={showCompanyNameValidationError || undefined}
                    aria-describedby={
                        showCompanyNameValidationError ? FIELD.companyName.errorProps.id : undefined
                    }
                    value={companyInfo.companyName}
                    onChange={(e) => {
                        onChange({ companyName: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type="text"
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="companyName"
                    autoComplete="organization"
                    spellCheck={false}
                    helperText={
                        showCompanyNameValidationError
                            ? `${capitalize(t.company_name)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showCompanyNameValidationError ? FIELD.companyName.errorProps : undefined
                    }
                />
            </FormControl>
            <FormControl>
                <InputLabel
                    htmlFor={FIELD.contactPerson.id}
                    error={showContactPersonValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.main_contact)}
                </InputLabel>
                <TextField
                    id={FIELD.contactPerson.id}
                    required={true}
                    error={showContactPersonValidationError}
                    aria-invalid={showContactPersonValidationError || undefined}
                    aria-describedby={
                        showContactPersonValidationError
                            ? FIELD.contactPerson.errorProps.id
                            : undefined
                    }
                    value={companyInfo.contactPerson}
                    onChange={(e) => {
                        onChange({ contactPerson: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type="text"
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="contactPerson"
                    autoComplete="name"
                    spellCheck={false}
                    helperText={
                        showContactPersonValidationError
                            ? `${capitalize(t.main_contact)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showContactPersonValidationError
                            ? FIELD.contactPerson.errorProps
                            : undefined
                    }
                />
            </FormControl>
            <FormControl>
                <InputLabel
                    htmlFor={FIELD.orgNumber.id}
                    error={showOrganizationNumberValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.org_number)}
                </InputLabel>
                <TextField
                    id={FIELD.orgNumber.id}
                    required={true}
                    error={showOrganizationNumberValidationError}
                    aria-invalid={showOrganizationNumberValidationError || undefined}
                    aria-describedby={
                        showOrganizationNumberValidationError
                            ? FIELD.orgNumber.errorProps.id
                            : undefined
                    }
                    value={companyInfo.orgNumber}
                    onChange={(e) => {
                        onChange({ orgNumber: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="orgNumber"
                    spellCheck={false}
                    helperText={
                        showOrganizationNumberValidationError
                            ? `${capitalize(t.org_number)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showOrganizationNumberValidationError
                            ? FIELD.orgNumber.errorProps
                            : undefined
                    }
                />
            </FormControl>

            <CountrySelect
                country={companyInfo.country}
                onChange={(country: Iso2Code) => {
                    onChange({
                        country,
                        phone: { ...companyInfo.phone, dialCode: getDialcodes()[country] },
                    });
                }}
                secondaryColor={true}
            />

            <FormControl>
                <InputLabel
                    error={showAddressValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.address)}
                </InputLabel>
                <TextField
                    error={showAddressValidationError}
                    aria-invalid={showAddressValidationError || undefined}
                    aria-describedby={
                        showAddressValidationError ? FIELD.address.errorProps.id : undefined
                    }
                    required={true}
                    value={companyInfo.address}
                    onChange={(e) => {
                        onChange({ address: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    name="address"
                    autoComplete="street-address"
                    spellCheck={false}
                    helperText={
                        showAddressValidationError
                            ? `${capitalize(t.address)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showAddressValidationError ? FIELD.address.errorProps : undefined
                    }
                />
            </FormControl>
            <FormControl>
                <InputLabel
                    error={showPostCodeValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.post_code)}
                </InputLabel>
                <TextField
                    error={showPostCodeValidationError}
                    aria-invalid={showPostCodeValidationError || undefined}
                    aria-describedby={
                        showPostCodeValidationError ? FIELD.postCode.errorProps.id : undefined
                    }
                    required={true}
                    value={companyInfo.postCode}
                    onChange={(e) => {
                        onChange({ postCode: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    name="postCode"
                    autoComplete="postal-code"
                    spellCheck={false}
                    helperText={
                        showPostCodeValidationError
                            ? `${capitalize(t.post_code)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showPostCodeValidationError ? FIELD.postCode.errorProps : undefined
                    }
                />
            </FormControl>
            <FormControl>
                <InputLabel
                    error={showCityValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.city)}
                </InputLabel>
                <TextField
                    error={showCityValidationError}
                    aria-invalid={showCityValidationError || undefined}
                    aria-describedby={
                        showCityValidationError ? FIELD.city.errorProps.id : undefined
                    }
                    required={true}
                    value={companyInfo.city}
                    onChange={(e) => {
                        onChange({ city: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    name="city"
                    autoComplete="address-level2"
                    spellCheck={false}
                    helperText={
                        showCityValidationError
                            ? `${capitalize(t.city)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showCityValidationError ? FIELD.city.errorProps : undefined
                    }
                />
            </FormControl>

            <PhoneInput
                phoneNumber={companyInfo.phone}
                errorPrefix={showPhoneDialCodeValidationError}
                errorNumber={showPhoneNumberValidationError}
                onChange={(phone: PhoneNumber) => {
                    onChange({ phone });
                }}
            ></PhoneInput>

            <FormControl>
                <InputLabel
                    htmlFor={FIELD.email.id}
                    error={showEmailValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.email)}
                </InputLabel>
                <TextField
                    id={FIELD.email.id}
                    error={showEmailValidationError}
                    aria-invalid={showEmailValidationError || undefined}
                    aria-describedby={
                        showEmailValidationError ? FIELD.email.errorProps.id : undefined
                    }
                    required={true}
                    value={companyInfo.email}
                    onChange={(e) => {
                        if (e.target instanceof HTMLInputElement)
                            onChange({ email: e.target.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="email"
                    autoComplete="email"
                    // type="text" (not "email"), so the browser would spellcheck the address.
                    spellCheck={false}
                    helperText={
                        showEmailValidationError
                            ? `${capitalize(t.email)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showEmailValidationError ? FIELD.email.errorProps : undefined
                    }
                />
            </FormControl>
            <Box role="group" aria-label={t.consents}>
                <FormControl required={true} error={showHasAcceptedPrivacyValidationError}>
                    <FormControlLabel
                        sx={[showHasAcceptedPrivacyValidationError && errorStyles]}
                        control={
                            <Checkbox
                                required={true}
                                sx={[showHasAcceptedPrivacyValidationError && errorStyles]}
                                color="secondary"
                                checked={companyInfo.hasAcceptedPrivacy}
                                onChange={(e) => {
                                    onChange({ hasAcceptedPrivacy: e.target.checked });
                                }}
                                inputProps={
                                    showHasAcceptedPrivacyValidationError
                                        ? FIELD.privacy.invalidProps
                                        : undefined
                                }
                            />
                        }
                        labelPlacement="end"
                        label={
                            (
                                <Typography color="textSecondary" variant="body1">
                                    {capitalize(t.i_have_read_and_accepted_the)}{' '}
                                    <Link
                                        color="primary"
                                        href={configurations.privacyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {capitalize(t.privacy_policy)}
                                    </Link>
                                </Typography>
                            ) as JSX.Element
                        }
                    />
                    {showHasAcceptedPrivacyValidationError && (
                        <FormHelperText id={FIELD.privacy.errorId} role="alert">
                            {t.must_accept_privacy_policy}
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl required={true} error={showHasAcceptedTermsValidationError}>
                    <FormControlLabel
                        sx={[showHasAcceptedTermsValidationError && errorStyles, { mt: 0 }]}
                        control={
                            <Checkbox
                                sx={[showHasAcceptedTermsValidationError && errorStyles]}
                                checked={companyInfo.hasAcceptedTerms}
                                color="secondary"
                                onChange={(e) => {
                                    onChange({ hasAcceptedTerms: e.target.checked });
                                }}
                                inputProps={
                                    showHasAcceptedTermsValidationError
                                        ? FIELD.terms.invalidProps
                                        : undefined
                                }
                            />
                        }
                        labelPlacement="end"
                        label={
                            (
                                <Typography color="textSecondary" variant="body1">
                                    {capitalize(t.i_have_read_and_accepted_the)}{' '}
                                    <Link
                                        color="primary"
                                        href={configurations.termsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {capitalize(t.terms_and_conditions)}
                                    </Link>
                                </Typography>
                            ) as JSX.Element
                        }
                        color="textSecondary"
                    />
                    {showHasAcceptedTermsValidationError && (
                        <FormHelperText id={FIELD.terms.errorId} role="alert">
                            {t.must_accept_terms_and_conditions}
                        </FormHelperText>
                    )}
                </FormControl>
                {configurations.enableNewsletter && (
                    <FormControl required={false}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={companyInfo.receiveNewsletter}
                                    color="secondary"
                                    onChange={(e) => {
                                        onChange({ receiveNewsletter: e.target.checked });
                                    }}
                                />
                            }
                            labelPlacement="end"
                            label={
                                (
                                    <Typography color="textSecondary" variant="body1">
                                        {capitalize(t.iWishToRecieveNewsletters)}
                                    </Typography>
                                ) as JSX.Element
                            }
                            color="textSecondary"
                        />
                    </FormControl>
                )}
            </Box>
        </Fragment>
    );
}
